import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { PagedResponse } from 'types/domainTypes';
import { ShrinkToIconButton } from '../../components/ShrinkToIconButton/ShrinkToIconButton';
import CenteredComponentView from 'components/CenteredComponentView/CenteredComponentView';
import { ProjectStack } from './ProjectStack';

interface NoProjectViewProps {
  onNewProjectClick: () => void;
  search: string;
}

function NoProjectsView({ onNewProjectClick, search }: NoProjectViewProps): ReactElement {
  const { t } = useTranslation();

  return (
    <CenteredComponentView>
      <Stack direction="column" justifyContent="center" alignItems="center" spacing={4} data-testid="noExistingProjectsDisplay">
        {search ? (
          <Typography variant="h6">{t('projects-page.no-projects-found-label')}</Typography>
        ) : (
          <>
            <Typography variant="h6">{t('projects-page.no-existing-projects-label')}</Typography>
            <ShrinkToIconButton
              data-testid="projectListNewProjectButton"
              hideTextWhenXS={false}
              buttonText={t('projects-page.new-project-button')}
              icon={<AddIcon sx={{ color: '#b39a4d' }} />}
              onClick={onNewProjectClick}
              variant="contained"
            />
          </>
        )}
      </Stack>
    </CenteredComponentView>
  );
}

interface ProjectListDisplayProps {
  onNewProjectClick: () => void;
  response?: PagedResponse;
  search: string;
}

export function ProjectListDisplay({ onNewProjectClick, response, search }: ProjectListDisplayProps): ReactElement {
  const projectsCount = response == null ? 0 : response.total;

  return (
    <Box data-testid={'project-list-display-wrapper'} data-projectcount={projectsCount}>
      {response == null || response.data.length === 0 ? (
        <NoProjectsView onNewProjectClick={onNewProjectClick} search={search} />
      ) : (
        <ProjectStack pageData={response} />
      )}
    </Box>
  );
}
